import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n/context';
import { RelayClient } from '../net/RelayClient';
import type { RelayError, RelayPhase } from '../net/RelayClient';
import { clientId, relayBaseUrl } from '../net/config';
import { ROOM_CODE_LENGTH, isRoomCode, normalizeRoomCode } from '../net/room';
import type { Transport } from '../net/Transport';
import { knownMapId } from '../game/netMatch';
import { DEFAULT_MAP_ID, MAP_DEFS } from '../content/maps';
import type { MapId } from '../content/maps';
import type { MatchConfig } from '../sim/types';
import type { TranslationKey } from '../i18n/he';

/*
 * הלובי.
 *
 * **הוא עושה דבר אחד: להביא שני אנשים לאותו `MatchConfig`.** ברגע שזה קרה הוא
 * מוסר את התעבורה ויוצא מהתמונה — ומכאן `NetBattle` הוא הבעלים. המסירה המפורשת
 * (`detach`) היא מה שמונע מפירוק הרכיב לסגור את הסוקט בדיוק כשהקרב מתחיל.
 *
 * **מסך אחד וארבעה מצבים, בלי ניתוב ובלי מכונת מצבים משלו.** מצב הלובי *הוא*
 * ה-`RelayPhase` שמגיע מהלקוח; העתקה שלו ל-state מקומי הייתה מקור אמת שני שיכול
 * לסתור את הראשון.
 *
 * **הלקוח נוצר בלחיצה ולא בהרכבה.** זה לא סגנון: ‏`StrictMode` מריץ אפקטים
 * פעמיים בפיתוח, ולקוח שנוצר ב-`useEffect` היה פותח שני סוקטים — כלומר תופס את
 * שני המושבים בחדר ומשאיר את היריב מחוץ לדלת.
 */

export interface NetHandoff {
    readonly transport: Transport;
    readonly config: MatchConfig;
    readonly localPlayer: number;
    readonly mapId: MapId;
    readonly room: string;
}

/**
 * בניית הלקוח. מוזרקת בטסטים — **אותו דפוס** כמו ה-factory של הסוקט והשעון
 * ב-`WebSocketTransport`. `null` פירושו "אין שרת מוגדר בבנייה הזו".
 */
export type RelayFactory = (onPhase: (phase: RelayPhase) => void) => RelayClient | null;

const defaultRelay: RelayFactory = (onPhase) => {
    const baseUrl = relayBaseUrl();
    if (baseUrl === null) return null;
    return new RelayClient({ baseUrl, clientId: clientId(), onPhase });
};

interface LobbyProps {
    readonly onStart: (handoff: NetHandoff) => void;
    readonly onBack: () => void;
    readonly relay?: RelayFactory;
}

const ERROR_KEYS: Readonly<Record<RelayError, TranslationKey>> = {
    noServer: 'lobby_error_no_server',
    network: 'lobby_error_network',
    notFound: 'lobby_error_not_found',
    full: 'lobby_error_full',
    badStart: 'lobby_error_bad_start',
    lost: 'lobby_error_lost',
};

export function Lobby({ onStart, onBack, relay = defaultRelay }: LobbyProps) {
    const { t } = useI18n();
    const clientRef = useRef<RelayClient | null>(null);
    /** נצרב פעם אחת: מסירה כפולה של אותה תעבורה מחזירה null ותיראה כניתוק. */
    const handedOff = useRef(false);

    const [phase, setPhase] = useState<RelayPhase>({ kind: 'idle' });
    const [code, setCode] = useState('');
    const [mapId, setMapId] = useState<MapId>(DEFAULT_MAP_ID);

    // פירוק המסך סוגר את הסוקט — אלא אם כבר נמסר לקרב. `dispose` יודע להבחין.
    useEffect(() => () => clientRef.current?.dispose(), []);

    const ensureClient = useCallback((): RelayClient | null => {
        if (clientRef.current) return clientRef.current;
        const client = relay(setPhase);
        if (client === null) {
            setPhase({ kind: 'error', reason: 'noServer' });
            return null;
        }
        clientRef.current = client;
        return client;
    }, [relay]);

    const reset = useCallback(() => {
        clientRef.current?.dispose();
        clientRef.current = null;
        setPhase({ kind: 'idle' });
    }, []);

    /*
     * המעבר לקרב יושב באפקט ולא בתוך `onPhase`: המסירה משנה את הבעלות על הסוקט
     * ומחליפה מסך, וזה יותר מדי בשביל להריץ מתוך callback של רשת באמצע רינדור.
     */
    useEffect(() => {
        if (phase.kind !== 'started' || handedOff.current) return;
        const client = clientRef.current;
        if (!client) return;

        /*
         * המפה מאומתת **כאן** ולא בשרת. ה-relay מעביר את המחרוזת בלי לדעת אילו
         * מפות קיימות — בכוונה — ולכן זו הנקודה שבה גרסה ישנה של המשחק מגלה שהיא
         * לא מכירה את המפה שנבחרה, במקום לקרוס בבניית המצב.
         */
        const known = knownMapId(phase.config.mapId);
        if (known === null) {
            setPhase({ kind: 'error', reason: 'badStart' });
            return;
        }
        const transport = client.detach();
        if (transport === null) {
            setPhase({ kind: 'error', reason: 'lost' });
            return;
        }
        handedOff.current = true;
        onStart({
            transport,
            config: phase.config,
            localPlayer: phase.localPlayer,
            mapId: known,
            room: phase.room,
        });
    }, [phase, onStart]);

    const typed = normalizeRoomCode(code);
    const busy = phase.kind === 'connecting' || phase.kind === 'waiting';

    return (
        <div className="menu lobby">
            <div className="menu__brand">
                <h1 className="menu__title">{t('lobby_title')}</h1>
                <p className="menu__tagline">{t('lobby_tagline')}</p>
            </div>

            {phase.kind === 'error' && (
                <p className="lobby__error" role="alert">
                    {t(ERROR_KEYS[phase.reason])}
                </p>
            )}

            {phase.kind === 'connecting' && (
                <p className="lobby__note" role="status">
                    {t('lobby_connecting')}
                </p>
            )}

            {phase.kind === 'waiting' ? (
                <div className="lobby__waiting" role="status">
                    <span className="lobby__codeLabel">{t('lobby_code_label')}</span>
                    {/*
                        הקוד הוא מה שנאמר בטלפון, ולכן הוא הדבר הגדול היחיד במסך.
                        `.num` מבודד אותו כיוונית — הוא לטיני בתוך מסך עברי.
                    */}
                    <strong className="lobby__code num">{phase.room}</strong>
                    <span className="lobby__note">{t('lobby_waiting_hint')}</span>
                </div>
            ) : (
                phase.kind !== 'started' && (
                    <>
                        <div className="menu__difficulty">
                            <span className="menu__difficultyLabel">{t('lobby_map')}</span>
                            {/*
                                גולש לשורות. בורר הקושי שלצידו הוא תמיד שלושה
                                שבבים, אבל **רשימת המפות גדלה עם התוכן** — וששת
                                השבבים של היום כבר לא נכנסים ל-375px בשורה אחת.
                            */}
                            <div className="menu__difficultyOptions lobby__maps">
                                {MAP_DEFS.map((map) => (
                                    <button
                                        key={map.id}
                                        className={
                                            map.id === mapId ? 'menu__chip menu__chip--on' : 'menu__chip'
                                        }
                                        type="button"
                                        aria-pressed={map.id === mapId}
                                        disabled={busy}
                                        onClick={() => setMapId(map.id)}
                                    >
                                        {t(map.nameKey)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="menu__actions">
                            <button
                                className="menu__button menu__button--primary"
                                type="button"
                                disabled={busy}
                                onClick={() => void ensureClient()?.create(mapId)}
                            >
                                <span>{t('lobby_create')}</span>
                                <span className="menu__buttonNote">{t('lobby_create_note')}</span>
                            </button>

                            <div className="lobby__join">
                                <label className="lobby__joinLabel" htmlFor="lobby-code">
                                    {t('lobby_join_label')}
                                </label>
                                <input
                                    id="lobby-code"
                                    className="lobby__input num"
                                    /*
                                        הקלט מנורמל בכל הקלדה ולא בשליחה: הקוד נאמר
                                        בקול, ומי שמקליד אותו באותיות קטנות או עם מקף
                                        צריך לראות מיד שזה בסדר — לא לגלות בשגיאה.
                                    */
                                    value={typed}
                                    onChange={(event) => setCode(normalizeRoomCode(event.target.value))}
                                    maxLength={ROOM_CODE_LENGTH}
                                    autoComplete="off"
                                    autoCapitalize="characters"
                                    spellCheck={false}
                                    inputMode="text"
                                    disabled={busy}
                                    placeholder={'—'.repeat(ROOM_CODE_LENGTH)}
                                />
                                <button
                                    className="menu__button"
                                    type="button"
                                    disabled={busy || !isRoomCode(typed)}
                                    onClick={() => ensureClient()?.join(typed)}
                                >
                                    {t('lobby_join')}
                                </button>
                            </div>
                        </div>
                    </>
                )
            )}

            <div className="menu__footer">
                {phase.kind === 'error' ? (
                    <button className="menu__lang" type="button" onClick={reset}>
                        {t('lobby_retry')}
                    </button>
                ) : null}
                <button
                    className="menu__lang"
                    type="button"
                    onClick={() => {
                        clientRef.current?.dispose();
                        clientRef.current = null;
                        onBack();
                    }}
                >
                    {t('lobby_back')}
                </button>
            </div>
        </div>
    );
}
